// SAM-1304 — event picker for a pass of an event (tenant admin and tenant Front Desk).
//
// Same shape as ServiceSelectControl (a native select in a `.fc` field); Tailwind + tokens only, no
// baseui/styletron (SAM-175 freeze). It lists the tenant's events a pass can be given for (eventPass.ts).
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { defaultInstance } from '../../../api/httpClient';
import type { Event } from '../../../hooks/useEvents';
import { useT } from '../../../i18n/admin';
import { eventDisplayName, filterOfferableEvents, toEventPassChoice, type EventPassChoice } from '../eventPass';

type Props = {
  value: EventPassChoice | null;
  onChange: (event: EventPassChoice | null) => void;
  disabled?: boolean;
};

// Not useEvents(): that list is narrowed to the current business for non-admins, and the tenant Front Desk has none.
const useTenantPublishedEvents = () =>
  useQuery({
    queryKey: ['events-for-pass'],
    queryFn: async (): Promise<Event[]> => {
      const response = await defaultInstance({
        method: 'GET',
        url: '/event?status=published&order_by=start_date&order_direction=DESC&limit=200&expand=event_type,event_type_id',
      });
      return (response as any)?.data?.data ?? (response as any)?.data ?? [];
    },
    staleTime: 30000,
  });

export const EventSelectControl = ({ value, onChange, disabled = false }: Props) => {
  const { data, isLoading, error } = useTenantPublishedEvents();
  const { t, lang } = useT();
  const events = useMemo(() => filterOfferableEvents(data), [data]);

  const placeholder = isLoading
    ? t('booking.event.loadingShort')
    : error
    ? t('booking.event.loadError')
    : events.length === 0
    ? t('booking.event.none')
    : t('booking.event.select');

  return (
    <div className="fc">
      <div className="relative">
        <select
          aria-label={t('booking.access.event')}
          value={value?.id || ''}
          disabled={disabled || isLoading || !!error || events.length === 0}
          onChange={(e) => {
            const selected = events.find((ev) => ev.id === e.target.value);
            onChange(selected ? toEventPassChoice(selected, lang) : null);
          }}
        >
          <option value="" disabled>{placeholder}</option>
          {events.map((event) => (
            <option key={event.id} value={event.id}>{eventDisplayName(event, lang)}</option>
          ))}
        </select>
      </div>
      {isLoading && (
        <div className="cap" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Loader2 size={12} className="animate-spin" /> {t('booking.event.loading')}
        </div>
      )}
    </div>
  );
};
